import React, { useState, useEffect, useMemo } from 'react';
import { HINDI_RECIPES, STATE_HINDI_MAP } from '../hindi-recipes';
import { TAMIL_RECIPES, STATE_TAMIL_MAP } from '../tamil-recipes';
import { TELUGU_RECIPES, STATE_TELUGU_MAP } from '../telugu-recipes';
import { ARABIC_RECIPES, STATE_ARABIC_MAP } from '../arabic-recipes';
import { getRecipeImage } from '../recipe-images';
import {
  getCoreDishTitle,
  getStateStyleLabel,
  getStateIcon
} from '../recipe-variants';

const PANTRY_SUGGESTIONS = [
  { id: 'rice', en: 'Rice', ta: 'அரிசி', hi: 'चावल', te: 'బియ్యం', ar: 'أرز' },
  { id: 'toor dal', en: 'Toor Dal', ta: 'துவரம் பருப்பு', hi: 'अरहर दाल', te: 'కందిపప్పు', ar: 'عدس أصفر' },
  { id: 'urad dal', en: 'Urad Dal', ta: 'உளுத்தம் பருப்பு', hi: 'उड़द दाल', te: 'మినప్పప్పు', ar: 'عدس أوراد' },
  { id: 'moong dal', en: 'Moong Dal', ta: 'பாசிப்பருப்பு', hi: 'मूंग दाल', te: 'పెసరపప్పు', ar: 'عدس ماش' },
  { id: 'wheat flour', en: 'Atta / Wheat', ta: 'கோதுமை மாவு', hi: 'गेहूं का आटा', te: 'గోధుమ పిండి', ar: 'دقيق قمح' },
  { id: 'rava', en: 'Rava / Sooji', ta: 'ரவை', hi: 'सूजी (रवा)', te: 'బొంబాయి రవ్వ', ar: 'سميد' },
  { id: 'potato', en: 'Potato', ta: 'உருளைக்கிழங்கு', hi: 'आलू', te: 'బంగాళాదుంప', ar: 'بطاطس' },
  { id: 'tomato', en: 'Tomato', ta: 'தக்காளி', hi: 'टमाटर', te: 'టమోటా', ar: 'طماطم' },
  { id: 'onion', en: 'Onion', ta: 'வெங்காயம்', hi: 'प्याज', te: 'ఉల్లిపాయ', ar: 'بصل' },
  { id: 'garlic', en: 'Garlic', ta: 'பூண்டு', hi: 'लहसुन', te: 'వెల్లుల్లి', ar: 'ثوم' },
  { id: 'ginger', en: 'Ginger', ta: 'இஞ்சி', hi: 'अदरक', te: 'అల్లం', ar: 'زنجبيل' },
  { id: 'green chillies', en: 'Green Chilli', ta: 'பச்சை மிளகாய்', hi: 'हरी मिर्च', te: 'పచ్చిమిర్చి', ar: 'فلفل أخضر' },
  { id: 'curry leaves', en: 'Curry Leaves', ta: 'கறிவேப்பிலை', hi: 'कढ़ी पत्ता', te: 'కరివేపాకు', ar: 'أوراق كاري' },
  { id: 'mustard seeds', en: 'Mustard Seeds', ta: 'கடுகு', hi: 'राई/सरसों', te: 'ఆవాలు', ar: 'خردل' },
  { id: 'cumin seeds', en: 'Cumin', ta: 'சீரகம்', hi: 'जीरा', te: 'జీలకర్ర', ar: 'كمون' },
  { id: 'turmeric powder', en: 'Turmeric', ta: 'மஞ்சள் தூள்', hi: 'हल्दी पाउडर', te: 'పసుపు', ar: 'كركم' },
  { id: 'sambar powder', en: 'Sambar Powder', ta: 'சாம்பார் பொடி', hi: 'सांभर मसाला', te: 'సాంబార్ పొడి', ar: 'بهارات سامبار' },
  { id: 'curd', en: 'Curd / Yogurt', ta: 'தயிர்', hi: 'ताज़ा दही', te: 'పెరుగు', ar: 'زبادي' },
  { id: 'ghee', en: 'Ghee', ta: 'சுத்தமான நெய்', hi: 'शुद्ध घी', te: 'నెయ్యి', ar: 'سمن بلدي' },
  { id: 'milk', en: 'Milk', ta: 'பால்', hi: 'दूध', te: 'పాలు', ar: 'حليب' },
  { id: 'paneer', en: 'Paneer', ta: 'பன்னீர்', hi: 'ताज़ा पनीर', te: 'పనీర్', ar: 'جبن بانير' },
  { id: 'coconut', en: 'Coconut', ta: 'தேங்காய்', hi: 'नारियल', te: 'కొబ్బరి', ar: 'جوز الهند' },
  { id: 'chicken', en: 'Chicken', ta: 'சிக்கன்', hi: 'चिकन', te: 'చికెన్', ar: 'دجاج' },
  { id: 'egg', en: 'Egg', ta: 'முட்டை', hi: 'अंडा', te: 'గుడ్డు', ar: 'بيض' },
  { id: 'fish', en: 'Fish', ta: 'மீன்', hi: 'मछली', te: 'చేపలు', ar: 'سمك' }
];

const INGREDIENT_NAMES_MAP = {
  ta: {
    'rice': 'அரிசி', 'toor dal': 'துவரம் பருப்பு', 'urad dal': 'உளுந்து', 'moong dal': 'பாசிப்பருப்பு',
    'potato': 'உருளைக்கிழங்கு', 'tomato': 'தக்காளி', 'onion': 'வெங்காயம்', 'garlic': 'பூண்டு',
    'ginger': 'இஞ்சி', 'green chillies': 'பச்சை மிளகாய்', 'curd': 'தயிர்', 'ghee': 'நெய்',
    'mustard seeds': 'கடுகு', 'curry leaves': 'கறிவேப்பிலை', 'coriander': 'கொத்தமல்லி',
    'coconut': 'தேங்காய்', 'sambar powder': 'சாம்பார் பொடி', 'paneer': 'பன்னீர்',
    'chicken': 'சிக்கன்', 'egg': 'முட்டை', 'fish': 'மீன்', 'rava': 'ரவை', 'sooji': 'ரவை',
    'wheat flour': 'கோதுமை மாவு', 'atta': 'கோதுமை மாவு', 'drumstick': 'முருங்கைக்காய்',
    'carrot': 'கேரட்', 'beans': 'பீன்ஸ்', 'peas': 'பட்டாணி', 'brinjal': 'கத்திரிக்காய்'
  },
  hi: {
    'rice': 'चावल', 'toor dal': 'अरहर दाल', 'urad dal': 'उड़द दाल', 'moong dal': 'मूंग दाल',
    'potato': 'आलू', 'tomato': 'टमाटर', 'onion': 'प्याज', 'garlic': 'लहसुन',
    'ginger': 'अदरक', 'green chillies': 'हरी मिर्च', 'curd': 'दही', 'ghee': 'घी',
    'mustard seeds': 'राई', 'curry leaves': 'कढ़ी पत्ता', 'coriander': 'धनिया',
    'coconut': 'नारियल', 'sambar powder': 'सांभर मसाला', 'paneer': 'पनीर',
    'chicken': 'चिकन', 'egg': 'अंडा', 'fish': 'मछली', 'rava': 'सूजी', 'sooji': 'सूजी',
    'wheat flour': 'गेहूं का आटा', 'atta': 'गेहूं का आटा', 'drumstick': 'सहजन',
    'carrot': 'गाजर', 'beans': 'बीन्स', 'peas': 'मटर', 'brinjal': 'बैंगन'
  },
  te: {
    'rice': 'బియ్యం', 'toor dal': 'కందిపప్పు', 'urad dal': 'మినప్పప్పు', 'moong dal': 'పెసరపప్పు',
    'potato': 'బంగాళాదుంప', 'tomato': 'టమోటా', 'onion': 'ఉల్లిపాయ', 'garlic': 'వెల్లుల్లి',
    'ginger': 'అల్లం', 'green chillies': 'పచ్చిమిర్చి', 'curd': 'పెరుగు', 'ghee': 'నెయ్యి',
    'mustard seeds': 'ఆవాలు', 'curry leaves': 'కరివేపాకు', 'coriander': 'కొత్తిమీర',
    'coconut': 'కొబ్బరి', 'sambar powder': 'సాంబార్ పొడి', 'paneer': 'పనీర్',
    'chicken': 'చికెన్', 'egg': 'గుడ్డు', 'fish': 'చేపలు', 'rava': 'రవ్వ', 'sooji': 'రవ్వ',
    'wheat flour': 'గోధుమ పిండి', 'atta': 'గోధుమ పిండి', 'drumstick': 'మునక్కాయ',
    'carrot': 'క్యారెట్', 'beans': 'బీన్స్', 'peas': 'బఠాణీలు', 'brinjal': 'వంకాయ'
  },
  ar: {
    'rice': 'أرز', 'toor dal': 'عدس أصفر', 'urad dal': 'عدس أوراد', 'moong dal': 'عدس ماش',
    'potato': 'بطاطس', 'tomato': 'طماطم', 'onion': 'بصل', 'garlic': 'ثوم',
    'ginger': 'زنجبيل', 'green chillies': 'فلفل أخضر', 'curd': 'زبادي', 'ghee': 'سمن',
    'mustard seeds': 'خردل', 'curry leaves': 'أوراق كاري', 'coriander': 'كزبرة',
    'coconut': 'جوز هند', 'sambar powder': 'بهارات سامبار', 'paneer': 'بانير',
    'chicken': 'دجاج', 'egg': 'بيض', 'fish': 'سمك', 'rava': 'سميد', 'sooji': 'سميد',
    'wheat flour': 'دقيق قمح', 'atta': 'دقيق قمح', 'drumstick': 'مورينجا',
    'carrot': 'جزر', 'beans': 'فاصوليا', 'peas': 'بازلاء', 'brinjal': 'باذنجان'
  }
};

export default function PantryChef({ t, lang, onOpenRecipeModal }) {
  const [pantryItems, setPantryItems] = useState([
    'rice', 'toor dal', 'potato', 'tomato', 'onion', 'curd', 'curry leaves', 'mustard seeds'
  ]);
  const [inputVal, setInputVal] = useState('');
  const [matches, setMatches] = useState([]);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [loading, setLoading] = useState(false);

  // Group pantry match results by base dish
  const groupedMatches = useMemo(() => {
    if (!matches || !Array.isArray(matches)) return [];
    const map = {};
    matches.forEach(m => {
      const r = m.recipe;
      if (!r) return;
      const core = r.core_dish || r.meal || 'other';
      if (!map[core]) {
        map[core] = { coreDish: core, variants: [] };
      }
      map[core].variants.push(m);
    });
    // Sort each group's variants by matchPct desc
    Object.values(map).forEach(g => {
      g.variants.sort((a, b) => (b.matchPct || 0) - (a.matchPct || 0));
    });
    // Sort groups by their highest matchPct
    return Object.values(map).sort((a, b) => {
      const bestA = a.variants[0]?.matchPct || 0;
      const bestB = b.variants[0]?.matchPct || 0;
      return bestB - bestA;
    });
  }, [matches]);

  const isEng = lang === 'English';
  const isTa = lang === 'Tamil';
  const isHi = lang === 'Hindi';
  const isTe = lang === 'Telugu';
  const isAr = lang === 'Arabic';

  useEffect(() => {
    matchPantry();
  }, [pantryItems]);

  const matchPantry = async () => {
    if (pantryItems.length === 0) {
      setMatches([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/pantry/match', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: pantryItems })
      });
      const data = await res.json();
      setMatches(data.matches || []);
      setLoading(false);
    } catch (err) {
      console.error('Pantry match error:', err);
      setLoading(false);
    }
  };

  const addItem = (item) => {
    const clean = item.trim().toLowerCase();
    if (clean && !pantryItems.includes(clean)) {
      setPantryItems([...pantryItems, clean]);
    }
  };

  const removeItem = (item) => {
    setPantryItems(pantryItems.filter(i => i !== item));
  };

  const toggleItem = (itemId) => {
    if (pantryItems.includes(itemId)) {
      removeItem(itemId);
    } else {
      addItem(itemId);
    }
  };

  const getMinutesUnit = () => {
    if (isEng) return 'm';
    if (isTa) return ' நிமி';
    if (isHi) return ' मिनट';
    if (isTe) return ' నిమి';
    if (isAr) return ' دقيقة';
    return 'm';
  };

  const getDishTitle = (r) => {
    if (isTa) return TAMIL_RECIPES[r.id]?.meal_ta || r.meal_ta || r.meal;
    if (isHi) return HINDI_RECIPES[r.id]?.meal_hi || r.meal;
    if (isTe) return TELUGU_RECIPES[r.id]?.meal_te || r.meal;
    if (isAr) return ARABIC_RECIPES[r.id]?.meal_ar || r.meal;
    return r.meal;
  };

  const getComboDishes = (r) => {
    if (isTa && TAMIL_RECIPES[r.id]?.dishes_ta) {
      return TAMIL_RECIPES[r.id].dishes_ta.map(d => d.name_ta || d.name);
    }
    if (isHi && HINDI_RECIPES[r.id]?.dishes_hi) {
      return HINDI_RECIPES[r.id].dishes_hi.map(d => d.name_hi || d.name);
    }
    if (isTe && TELUGU_RECIPES[r.id]?.dishes_te) {
      return TELUGU_RECIPES[r.id].dishes_te.map(d => d.name_te || d.name);
    }
    if (isAr && ARABIC_RECIPES[r.id]?.dishes_ar) {
      return ARABIC_RECIPES[r.id].dishes_ar.map(d => d.name_ar || d.name);
    }
    if (r.dishes && r.dishes.length > 0) {
      return r.dishes.map(d => d.name);
    }
    return (r.meal || '').split('+').map(s => s.trim()).filter(Boolean);
  };

  const getDishState = (r) => {
    if (isTa) return STATE_TAMIL_MAP[r.state] || r.state;
    if (isHi) return STATE_HINDI_MAP[r.state] || r.state;
    if (isTe) return STATE_TELUGU_MAP[r.state] || r.state;
    if (isAr) return STATE_ARABIC_MAP[r.state] || r.state;
    return r.state;
  };

  const translateIngredient = (ing) => {
    if (isEng) return ing;
    const low = (ing || '').toLowerCase().trim();
    let map = INGREDIENT_NAMES_MAP.en;
    if (isTa) map = INGREDIENT_NAMES_MAP.ta;
    else if (isHi) map = INGREDIENT_NAMES_MAP.hi;
    else if (isTe) map = INGREDIENT_NAMES_MAP.te;
    else if (isAr) map = INGREDIENT_NAMES_MAP.ar;
    if (map) {
      for (const [k, v] of Object.entries(map)) {
        if (low === k || low.includes(k) || k.includes(low)) {
          return v;
        }
      }
    }
    return ing;
  };

  return (
    <section id="pantrySection" className="section">
      <div className="container">
        <div className="section-header">
          <span className="section-tag">{t.navPantry}</span>
          <h2 className="section-title">{t.pantryTitle}</h2>
          <p className="section-desc">{t.pantrySubtitle}</p>
        </div>

        <div className="pantry-card-wrapper">
          <div className="pantry-input-row">
            <input
              type="text"
              className="pantry-text-input"
              placeholder={t.pantryInputPlaceholder}
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addItem(inputVal);
                  setInputVal('');
                }
              }}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                addItem(inputVal);
                setInputVal('');
              }}
            >
              {t.pantryBtnAdd}
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setPantryItems(['rice', 'toor dal', 'potato', 'tomato', 'onion', 'curd', 'curry leaves'])}
            >
              {t.pantryBtnDefaults}
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setPantryItems([])}
            >
              {t.pantryBtnClear}
            </button>
          </div>

          <div className="pantry-suggestions">
            <div className="pantry-suggestions-title">{t.pantryTapToggle}</div>
            <div className="pantry-chips-cloud">
              {PANTRY_SUGGESTIONS.map(s => {
                const active = pantryItems.includes(s.id);
                const label = isTa ? s.ta : (isHi ? s.hi : (isTe ? s.te : (isAr ? s.ar : s.en)));
                return (
                  <button
                    key={s.id}
                    type="button"
                    className={`pantry-chip ${active ? 'active' : ''}`}
                    onClick={() => toggleItem(s.id)}
                  >
                    {active ? '✓ ' : '+ '}{label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="pantry-current-basket">
            <div className="basket-header">
              <span>{(t.pantryInBasket || 'Items in Kitchen Pantry ({count})').replace('{count}', pantryItems.length)}</span>
            </div>
            <div className="basket-chips">
              {pantryItems.map(item => (
                <span key={item} className="basket-chip">
                  <span className="basket-chip-name">{translateIngredient(item)}</span>
                  <button
                    type="button"
                    className="basket-chip-remove"
                    onClick={() => removeItem(item)}
                    title="Remove item"
                    aria-label={`Remove ${item}`}
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          </div>

          <h3 className="pantry-results-heading">{t.pantryResultsHeading}</h3>
          {loading ? (
            <div style={{ color: 'var(--text-muted)' }}>{t.pantryCalculating}</div>
          ) : groupedMatches.length === 0 ? (
            <div className="empty-state">
              <p>{t.pantryEmptyMatches}</p>
            </div>
          ) : (
            <div className="pantry-matches-grid">
              {groupedMatches.map(group => {
                let activeMatch = group.variants.find(v => v.recipe?.id === selectedVariants[group.coreDish]) || group.variants[0];
                const r = activeMatch.recipe;
                const statusColor = activeMatch.matchPct >= 70 ? 'match-high' : (activeMatch.matchPct >= 40 ? 'match-med' : 'match-low');
                const coreTitle = getCoreDishTitle(group.coreDish, lang);
                const dishTitle = getDishTitle(r);
                const dishImg = getRecipeImage(r);
                const allGroupVariants = group.variants.map(v => v.recipe);

                return (
                  <div key={group.coreDish} className="pantry-match-card">
                    {/* Card Media Header */}
                    <div
                      className="pantry-card-visual"
                      onClick={() => onOpenRecipeModal(r, 4, allGroupVariants)}
                      style={{ cursor: 'pointer' }}
                      title={`${coreTitle} - ${t.pantryBtnCookThis || 'Cook This'}`}
                    >
                      <img
                        src={dishImg}
                        alt={coreTitle}
                        className="pantry-match-img"
                        loading="lazy"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `/images/recipes/recipe_${r?.id || 1}.jpg`;
                        }}
                      />
                      <div className="pantry-card-overlay"></div>
                      <div className={`match-score-badge ${statusColor}`}>
                        ⚡ {activeMatch.matchPct}% {t.pantryMatchScore || 'Match'}
                      </div>
                      <span className="pantry-cuisine-tag">📍 {getStateStyleLabel(r.state, lang)}</span>
                    </div>

                    <div className="match-content">
                      <h4 className="pantry-card-heading">{coreTitle}</h4>
                      <div className="pantry-card-sub">
                        <span>{getStateIcon(r.state)} {dishTitle}</span>
                      </div>

                      {/* Regional Style Dropdown Selector */}
                      {group.variants.length > 1 && (
                        <div className="card-style-selector-row" onClick={(e) => e.stopPropagation()}>
                          <label className="card-style-selector-label">
                            <span className="style-selector-icon">📍</span>
                            <span>{isTa ? 'ஸ்டைல் தேர்வு:' : (isHi ? 'शैली चुनें:' : 'Select Style:')}</span>
                          </label>
                          <select
                            className="card-style-dropdown"
                            value={r.id}
                            onChange={(e) => {
                              const newId = Number(e.target.value);
                              setSelectedVariants(prev => ({ ...prev, [group.coreDish]: newId }));
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {group.variants.map(v => (
                              <option key={v.recipe.id} value={v.recipe.id}>
                                {getStateIcon(v.recipe.state)} {getStateStyleLabel(v.recipe.state, lang)} ({v.matchPct}% Match)
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Combination Dish Tags */}
                      <div className="pantry-combo-row">
                        <span className="pantry-combo-label">{isTa ? '🍽️ காம்போ:' : (isHi ? '🍽️ कॉम्बो:' : (isTe ? '🍽️ కాంబో:' : (isAr ? '🍽️ تشكيلة:' : '🍽️ Combo:')))}</span>
                        <span className="pantry-combo-dishes">{getComboDishes(r).join(' • ')}</span>
                      </div>

                      <p className="match-meta">
                        ⏱ {r.minutes}{getMinutesUnit()} | 🌿 {r.diet === 'Vegetarian' ? t.cardVegBadge : t.cardNonVegBadge}
                      </p>

                      <div className="missing-box">
                        {activeMatch.missing.length === 0 ? (
                          <span className="ready-badge">✔ {t.pantryAllIngsReady}</span>
                        ) : (
                          <>
                            <span className="missing-label">{t.pantryNeed}:</span>
                            <span className="missing-items">
                              {activeMatch.missing.slice(0, 3).map(i => translateIngredient(i)).join(', ')}
                              {activeMatch.missing.length > 3 ? ` +${activeMatch.missing.length - 3} ${t.pantryMore}` : ''}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="match-action">
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => onOpenRecipeModal(r, 4, allGroupVariants)}
                        style={{ width: '100%', justifyContent: 'center' }}
                      >
                        {t.pantryBtnCookThis}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
